import {
  Button,
  Card,
  Dialog,
  Form,
  FormButton,
  FormSubmitHandler,
  Separator,
  Table,
} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {useState} from 'react';

import {head, isNil, isNotNil} from 'ramda';

import {FinancingRateMatrixCell} from '@dms/api/vehicle';
import {CalculationValue} from '@dms/features/financial-products';
import i18n from '@dms/i18n';

import {BalloonVariantRow} from './BalloonVariantRow';
import {
  balloonFinancingCellFormSchema,
  BalloonFinancingCellFormValues,
} from './schemas/balloonFinancingCellFormSchema';
import {VehicleFinancingPricing} from './types/VehicleFinancingPricing';
import {calculateFinancingOffer, FinancingInsurance} from './utils/calculateFinancingOffer';
import {isConfiguredBalloonVariant} from './utils/financingCells/isConfiguredBalloonVariant';

interface BalloonFinancingCellDialogProps {
  isOpen: boolean;
  term: number;
  downPayment: number;
  balloonPayments: number[];
  initialBalloonPayment: number;
  activeBalloonPayment: number | null;
  cells: FinancingRateMatrixCell[];
  vehiclePrice: number;
  currency: string;
  pricing: VehicleFinancingPricing | null;
  insurance: FinancingInsurance;
  showCalculation?: boolean;
  setActiveVariantLabel?: string;
  isReadOnly: boolean;
  onClose: () => void;
  onSave: (cells: FinancingRateMatrixCell[], activeBalloonPayment: number | null) => void;
  onDelete: () => void;
}

export function BalloonFinancingCellDialog(props: BalloonFinancingCellDialogProps) {
  // The previewed variant only decides which calculation the dialog shows, so it stays out of the
  // form: it is never saved and never validated.
  const [previewBalloonPayment, setPreviewBalloonPayment] = useState(props.initialBalloonPayment);
  const formatCurrency = useFormatCurrency();

  const defaultValues: BalloonFinancingCellFormValues = {
    variants: props.balloonPayments.map((balloonPayment) => {
      const cell = props.cells.find((item) => item.balloonPayment === balloonPayment);

      return {
        balloonPayment,
        nominalRate: isNotNil(cell) ? Number(cell.nominalRate) : null,
        apr: isNotNil(cell) ? Number(cell.apr) : null,
      };
    }),
    activeBalloonPayment: props.activeBalloonPayment,
  };

  const handleSubmit: FormSubmitHandler<BalloonFinancingCellFormValues> = (values) => {
    const savedCells = values.variants.filter(isConfiguredBalloonVariant).map((variant) => ({
      term: props.term,
      downPayment: props.downPayment,
      balloonPayment: variant.balloonPayment,
      nominalRate: String(variant.nominalRate),
      apr: String(variant.apr),
    }));
    const savedActiveBalloonPayment = savedCells.some(
      (cell) => cell.balloonPayment === values.activeBalloonPayment
    )
      ? values.activeBalloonPayment
      : null;

    props.onSave(savedCells, savedActiveBalloonPayment);

    return Promise.resolve();
  };

  const downPaymentAmount = props.vehiclePrice * (props.downPayment / 100);

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={i18n.t('page.vehicle.financingAndLeasing.editVariant')}
      size="large"
    >
      <Form<BalloonFinancingCellFormValues>
        experimentalZodSchema={balloonFinancingCellFormSchema}
        mode="onChange"
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
      >
        {(control, formApi) => {
          const variants = formApi.watch('variants');
          const activeBalloonPayment = formApi.watch('activeBalloonPayment');
          const previewVariant =
            variants.find((variant) => variant.balloonPayment === previewBalloonPayment) ??
            head(variants);
          const calculation =
            isNil(previewVariant) || isNil(previewVariant.nominalRate)
              ? null
              : calculateFinancingOffer({
                  pricing: props.pricing,
                  term: props.term,
                  downPayment: props.downPayment,
                  balloonPayment: previewVariant.balloonPayment,
                  nominalRate: previewVariant.nominalRate,
                  insurance: props.insurance,
                });
          const balloonPaymentAmount =
            props.vehiclePrice * ((previewVariant?.balloonPayment ?? 0) / 100);

          // The paired required rule lives on the sibling rate, so editing one of them has to
          // revalidate the whole table for the error to appear and disappear as the user types.
          const revalidateVariants = () => {
            void formApi.trigger('variants');
          };

          const setActiveVariant = (balloonPayment: number) => {
            formApi.setValue('activeBalloonPayment', balloonPayment);
            setPreviewBalloonPayment(balloonPayment);
          };

          const clearVariant = (index: number, balloonPayment: number) => {
            formApi.setValue(`variants.${index}.nominalRate`, null);
            formApi.setValue(`variants.${index}.apr`, null);
            revalidateVariants();

            if (activeBalloonPayment === balloonPayment) {
              formApi.setValue(
                'activeBalloonPayment',
                variants.find(
                  (variant) =>
                    variant.balloonPayment !== balloonPayment && isConfiguredBalloonVariant(variant)
                )?.balloonPayment ?? null
              );
            }
          };

          return (
            <VStack spacing={4}>
              <HStack spacing={2} wrap>
                <Text color="secondary">
                  {i18n.t('page.vehicle.financingAndLeasing.termValue', {term: props.term})}
                </Text>
                <Text color="secondary">·</Text>
                <Text color="secondary">
                  {i18n.t('page.vehicle.financingAndLeasing.downPaymentValue', {
                    downPayment: props.downPayment,
                  })}
                </Text>
              </HStack>

              <VStack spacing={2}>
                <Heading size={5}>
                  {i18n.t('page.vehicle.financingAndLeasing.balloonPayment')}
                </Heading>
                <Box overflowX="auto">
                  <Box minWidth={146}>
                    <Table
                      variant="plain"
                      tableLayout="fixed"
                      columns={[
                        {width: 12},
                        {
                          width: 22,
                          element: (
                            <Text size="xSmall" color="secondary">
                              {i18n.t('page.vehicle.financingAndLeasing.balloonPayment')}
                            </Text>
                          ),
                        },
                        {
                          width: 32,
                          element: (
                            <Text size="xSmall" color="secondary">
                              {i18n.t('page.vehicle.financingAndLeasing.nominalRate')}
                            </Text>
                          ),
                        },
                        {
                          width: 32,
                          element: (
                            <Text size="xSmall" color="secondary">
                              {i18n.t('page.vehicle.financingAndLeasing.apr')}
                            </Text>
                          ),
                        },
                        {
                          width: 36,
                          element: (
                            <Text size="xSmall" color="secondary">
                              {i18n.t('page.vehicle.financingAndLeasing.resultingMonthlyRate')}
                            </Text>
                          ),
                        },
                        {width: 12},
                      ]}
                    >
                      {variants.map((variant, index) => (
                        <BalloonVariantRow
                          key={variant.balloonPayment}
                          control={control}
                          index={index}
                          variant={variant}
                          term={props.term}
                          downPayment={props.downPayment}
                          currency={props.currency}
                          pricing={props.pricing}
                          insurance={props.insurance}
                          setActiveVariantLabel={props.setActiveVariantLabel}
                          isReadOnly={props.isReadOnly}
                          isPreviewed={previewBalloonPayment === variant.balloonPayment}
                          isActive={activeBalloonPayment === variant.balloonPayment}
                          onPreview={() => setPreviewBalloonPayment(variant.balloonPayment)}
                          onSetActive={() => setActiveVariant(variant.balloonPayment)}
                          onRateChange={revalidateVariants}
                          onClear={() => clearVariant(index, variant.balloonPayment)}
                        />
                      ))}
                    </Table>
                  </Box>
                </Box>
              </VStack>

              <Show when={isNotNil(previewVariant) && (props.showCalculation ?? true)}>
                <VStack spacing={2}>
                  <HStack spacing={2} wrap>
                    <Heading size={5}>
                      {i18n.t('page.vehicle.financingAndLeasing.calculator')}
                    </Heading>
                    <Text color="secondary">·</Text>
                    <Text color="secondary">
                      {i18n.t('page.vehicle.financingAndLeasing.balloonValue', {
                        balloonPayment: previewVariant?.balloonPayment,
                      })}
                    </Text>
                  </HStack>
                  <Card variant="inlineGrey">
                    <VStack spacing={1}>
                      <CalculationValue
                        label={i18n.t('entity.vehicle.labels.sellingPrice')}
                        value={formatCurrency(props.vehiclePrice, props.currency)}
                      />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.downPayment')}
                        value={`${formatCurrency(downPaymentAmount, props.currency)} (${
                          props.downPayment
                        } %)`}
                      />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.term')}
                        value={i18n.t('page.vehicle.financingAndLeasing.monthsValue', {
                          term: props.term,
                        })}
                      />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.netCredit')}
                        value={
                          isNotNil(calculation)
                            ? formatCurrency(calculation.netCredit, props.currency)
                            : '—'
                        }
                      />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.balloonPayment')}
                        value={`${formatCurrency(balloonPaymentAmount, props.currency)} (${
                          previewVariant?.balloonPayment ?? 0
                        } %)`}
                      />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.apr')}
                        value={previewVariant?.apr === null ? '—' : `${previewVariant?.apr} %`}
                      />
                      <Separator spacing={1} />
                      <CalculationValue
                        label={i18n.t('page.vehicle.financingAndLeasing.resultingMonthlyRate')}
                        value={
                          isNotNil(calculation)
                            ? formatCurrency(calculation.monthlyRate, props.currency)
                            : '—'
                        }
                        isHighlighted
                      />
                    </VStack>
                  </Card>
                </VStack>
              </Show>

              <Show when={!props.isReadOnly}>
                <HStack width="100%" justify="space-between" align="center" spacing={4}>
                  <Box>
                    <Show when={props.cells.length > 0}>
                      <Button
                        title={i18n.t('general.actions.delete')}
                        leftIcon="action/delete"
                        variant="dangerOutlined"
                        onClick={props.onDelete}
                      />
                    </Show>
                  </Box>
                  <HStack spacing={2}>
                    <Button
                      title={i18n.t('general.actions.cancel')}
                      variant="secondary"
                      onClick={props.onClose}
                    />
                    <FormButton
                      control={control}
                      type="submit"
                      title={i18n.t('general.actions.save')}
                      isDisabled={!formApi.formState.isValid}
                    />
                  </HStack>
                </HStack>
              </Show>
            </VStack>
          );
        }}
      </Form>
    </Dialog>
  );
}
